import React, { type FC, memo, useCallback, useId, useMemo, useState } from "react";
import { cn } from "@heroui/react";

import { EntityOpenLink } from "./agentLinks";
import { DETAIL_GROUP_LABELS, reportLineText } from "./reportText";
import type { AgentDetailGroup, AgentDetailItem, AgentEntityRef } from "./runner/types";
import { useAgentEntityLabel } from "./useAgentEntityLabel";
import { useTranslation } from "@domain/intl/useTranslation";

export const MAX_DETAIL_ROWS = 50;

const GROUP_ORDER: AgentDetailGroup[] = ["done", "needsYou", "refused", "untouched"];

const groupTones: Record<AgentDetailGroup, string> = {
    done: "bg-success/15 text-success",
    needsYou: "bg-warning/15 text-warning",
    refused: "bg-danger/15 text-danger",
    untouched: "bg-default text-muted",
};

const EntityLabel: FC<{ entity: AgentEntityRef }> = ({ entity }) => {
    const label = useAgentEntityLabel(entity);
    return <span className="min-w-0 truncate">{label}</span>;
};

const DetailRow: FC<{ item: AgentDetailItem }> = ({ item }) => {
    const { t } = useTranslation();
    const outcome = reportLineText(t, item.outcome);
    return (
        <li
            data-testid="agent-detail-row"
            className="flex items-center gap-2 border-t border-separator py-1 text-xs first:border-t-0"
        >
            {item.ref && (
                <span className="flex min-w-0 items-center whitespace-pre">
                    <EntityLabel entity={item.ref} />
                    {item.target && (
                        <>
                            <span aria-hidden>{" → "}</span>
                            <EntityLabel entity={item.target} />
                        </>
                    )}
                </span>
            )}
            <span className={cn("text-muted", item.ref ? "ml-auto shrink-0 text-right" : "min-w-0")}>{outcome}</span>
            {item.ref && <EntityOpenLink entity={item.ref} />}
        </li>
    );
};

interface Props {
    items: AgentDetailItem[];
}

/** "Details ansehen" under a finished write: the affected items by group, at most MAX_DETAIL_ROWS rows. */
const AgentDetailsImpl: FC<Props> = ({ items }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const listId = useId();
    const handleToggle = useCallback(() => setExpanded((e) => !e), []);

    const { groups, rest } = useMemo(() => {
        const ordered = GROUP_ORDER.map((group) => ({ group, all: items.filter((i) => i.group === group) }));
        let left = MAX_DETAIL_ROWS;
        const shown = ordered.map(({ group, all }) => {
            const rows = all.slice(0, Math.max(0, left));
            left -= rows.length;
            return { group, total: all.length, rows };
        });
        return {
            groups: shown.filter((g) => g.rows.length > 0),
            rest: Math.max(0, items.length - MAX_DETAIL_ROWS),
        };
    }, [items]);

    if (items.length === 0) {
        return null;
    }

    return (
        <div className="mt-1.5">
            <button
                type="button"
                className="inline-flex items-center gap-1 text-sm text-accent underline underline-offset-2"
                aria-expanded={expanded}
                aria-controls={listId}
                onClick={handleToggle}
            >
                <svg
                    aria-hidden
                    viewBox="0 0 12 12"
                    className={cn("size-3 motion-safe:transition-transform", expanded && "rotate-90")}
                >
                    <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" />
                </svg>
                {t(expanded ? "app.agent.details.hide" : "app.agent.details.show")}
            </button>
            {expanded && (
                <div id={listId} className="mt-2 rounded-lg border border-separator bg-default/40 px-3 py-1">
                    {groups.map(({ group, total, rows }) => (
                        <section key={group} data-testid="agent-detail-group" data-group={group} className="py-1">
                            <h5 className="flex items-center gap-1.5 pt-1 text-xs font-semibold">
                                <span>{t(DETAIL_GROUP_LABELS[group])}</span>
                                <span className={cn("rounded-full px-1.5 text-[11px] font-medium", groupTones[group])}>
                                    {total}
                                </span>
                            </h5>
                            <ul className="m-0 list-none p-0">
                                {rows.map((item, i) => (
                                    <DetailRow
                                        key={`${item.ref?.kind ?? "line"}-${item.ref?.id ?? i}-${i}`}
                                        item={item}
                                    />
                                ))}
                            </ul>
                        </section>
                    ))}
                    {rest > 0 && (
                        <p className="py-1 text-xs text-muted">{t("app.agent.details.more", { count: rest })}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export const AgentDetails = memo(AgentDetailsImpl);
